<template>
  <!-- Previous template code remains the same until the filters section -->
  <div class="space-y-6">
    <div v-if="loading" class="flex justify-center py-12">
      <div class="relative w-20 h-20">
        <div class="absolute top-0 left-0 w-full h-full border-4 border-blue-200 dark:border-blue-900 rounded-full animate-pulse" />
        <div
          class="absolute top-0 left-0 w-full h-full border-4 border-transparent border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin" />
      </div>
    </div>

    <div v-if="show_object_type_form">
      <ObjectTypeForm
        :object-type="current_object_type"
        :loading="loading_object_types"
        @submit="show_object_type_form = false"
        @cancel="show_object_type_form = false" />
    </div>

    <template v-else>
      <!-- Origin Filters -->
      <div class="flex justify-between items-center mb-6">
        <div class="flex space-x-2 overflow-x-auto pb-2">
          <button
            class="px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150 ease-in-out whitespace-nowrap"
            :class="[
              !active_filter
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            ]"
            @click="active_filter = null">
            {{ $t("All Origins") }}
          </button>
          <button
            v-for="origin in available_origins"
            :key="origin.id"
            class="px-4 py-2 text-sm font-medium rounded-md capitalize transition-colors duration-150 ease-in-out whitespace-nowrap"
            :class="[
              active_filter === origin.origin
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            ]"
            @click="active_filter = origin.origin">
            {{ origin.origin_title || "Local" }}
          </button>
        </div>
        <button
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out transform hover:scale-105"
          @click="() => (show_object_type_form = true)">
          <Plus class="w-5 h-5 mr-2" />
          {{ $t("Add Object Type") }}
        </button>
      </div>

      <!-- Rest of the template remains the same -->
      <div
        v-if="$store.state.object_types?.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-h-[calc(100vh-300px)] overflow-y-auto relative scroll-container pb-6">
        <div
          v-for="otype in filtered_object_types"
          :key="otype.id"
          class="bg-white dark:bg-dark-box-background rounded-xl shadow-sm hover:shadow-md transition-all duration-200 ease-in-out border border-gray-100 dark:border-gray-800 overflow-hidden group">
          <!-- Card Header -->
          <div class="p-6 border-b border-gray-100 dark:border-gray-800">
            <div class="flex items-start justify-between">
              <div class="flex-grow">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {{ otype.label }}
                </h3>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  {{ otype.plural_label }}
                </p>
              </div>
              <div class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <button
                  class="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/50 dark:hover:text-blue-400 transition-colors duration-150"
                  @click="
                    () => {
                      current_object_type = otype.object_type
                      show_object_type_form = true
                    }
                  ">
                  <Pencil class="w-4 h-4" />
                </button>
                <button
                  class="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/50 dark:hover:text-red-400 transition-colors duration-150"
                  @click="showConfirmDelete(otype)">
                  <Trash2 class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- Card Body -->
          <div class="p-6 space-y-4">
            <!-- Type & Origin -->
            <div class="flex items-center space-x-3">
              <div class="flex items-center space-x-2">
                <Globe class="w-4 h-4 text-gray-400" />
                <span class="text-sm text-gray-600 dark:text-gray-400">{{ $t(otype.origin_title) || $t("Local") }}</span>
              </div>
              <div class="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
              <div class="flex items-center space-x-2">
                <Database class="w-4 h-4 text-gray-400" />
                <span class="text-sm text-gray-600 dark:text-gray-400">{{ $t(otype.origin_object_name) }}</span>
              </div>
            </div>

            <!-- Metrics Summary -->
            <div class="space-y-3">
              <!-- Total Metrics -->
              <div class="flex items-center justify-between">
                <span class="text-sm text-gray-600 dark:text-gray-400">
                  {{ $t("Total Metrics") }}
                </span>
                <span class="text-lg font-semibold text-gray-900 dark:text-white">
                  {{ getTotalMetricCount(otype.type) }}
                </span>
              </div>

              <!-- Metrics Distribution -->
              <div class="grid grid-cols-3 gap-2">
                <div class="flex flex-col items-center p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <span class="text-lg font-semibold text-blue-600 dark:text-blue-400">
                    {{ getMetricCount(otype.type, "custom_metric") }}
                  </span>
                  <span class="text-xs text-blue-600 dark:text-blue-400">{{ $t("Custom") }}</span>
                </div>
                <div class="flex flex-col items-center p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <span class="text-lg font-semibold text-green-600 dark:text-green-400">
                    {{ getMetricCount(otype.type, "computed_metric") }}
                  </span>
                  <span class="text-xs text-green-600 dark:text-green-400">{{ $t("Calculated") }}</span>
                </div>
                <div class="flex flex-col items-center p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                  <span class="text-lg font-semibold text-purple-600 dark:text-purple-400">
                    {{ getMetricCount(otype.type, "reference_metric") }}
                  </span>
                  <span class="text-xs text-purple-600 dark:text-purple-400">{{ $t("Reference") }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white dark:bg-dark-box-background rounded-xl shadow-sm p-12 text-center">
        <div class="flex flex-col items-center max-w-sm mx-auto">
          <div class="w-20 h-20 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-6">
            <Info class="w-10 h-10 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-3">
            {{ $t("No Object Types") }}
          </h3>
          <p class="text-base text-gray-500 dark:text-gray-400 mb-6">
            {{ $t("Get started by creating your first object type") }}
          </p>
          <button
            class="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out transform hover:scale-105"
            @click="() => (show_object_type_form = true)">
            <Plus class="w-5 h-5 mr-2" />
            {{ $t("Add Object Type") }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ObjectType } from "@/models/object_type"
import { Plus, Trash2, Pencil, Info, Database, Globe } from "lucide-vue-next"
import ObjectTypeForm from "@/views/dashboard/admin/metrics/ObjectTypeForm.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { utils } from "@/store/helpers"

export default {
  name: "ObjectTypeList",
  components: {
    ObjectTypeForm,
    Plus,
    Pencil,
    Trash2,
    Info,
    Database,
    Globe
  },
  inject: ["removeObjectTypeFromView"],
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      show_object_type_form: false,
      current_object_type: null,
      loading_object_types: false,
      active_filter: null
    }
  },
  computed: {
    sorted_object_types() {
      return [...this.$store.state.object_types.map((o) => ObjectType.of(o))].sort((a, b) => {
        return this.getTotalMetricCount(b.type) - this.getTotalMetricCount(a.type)
      })
    },
    available_origins() {
      return utils.uniqBy(this.sorted_object_types, "origin")
    },
    filtered_object_types() {
      if (!this.active_filter) {
        return this.sorted_object_types
      }
      return this.sorted_object_types.filter((otype) => otype.origin === this.active_filter)
    }
  },
  methods: {
    showConfirmDelete(objectType) {
      this.$emitter.emit(ALL_EMITTERS.GENERIC_DELETE_MODAL, {
        title: this.$t("Delete") + this.$t(` ${objectType.label} ${objectType.origin != "Local" ? `(${objectType.origin_title})` : ""}`),
        success_message: this.$t("ObjectType") + ` ${objectType.label} ` + this.$t("deleted successfully"),
        message:
          this.$t("Do you want to delete object type") +
          ` ${objectType.label} ?` +
          this.$t("Any related metrics or Records will deleted as well!"),
        action: () => {
          this.$store.dispatch("objects/deleteObjectType", objectType).then(() => {
            this.removeObjectTypeFromView(objectType.type)
          })
        }
      })
    },
    getMetricCount(objectType, metricClass) {
      return this.$store.state.metrics.filter((m) => m.object_type === objectType && m.metric_class === metricClass).length
    },
    getTotalMetricCount(objectType) {
      return this.$store.state.metrics.filter((m) => m.object_type === objectType).length
    }
  }
}
</script>

<style scoped>
.scroll-container {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.scroll-container::-webkit-scrollbar {
  width: 6px;
}

.scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}
</style>
